import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Box, Slider, Typography } from '@mui/material';
import { LocalVideoTrack, Participant, RemoteAudioTrack, RemoteParticipant, RemoteVideoTrack } from 'twilio-video';
import {
  // useIsRecording,
  useIsTrackEnabled,
  useIsTrackSwitchedOff,
  useIsGuestCall,
  useMutedParticipants,
  useParticipantIsReconnecting,
  useParticipantNQ,
  usePublications,
  useRoomHost,
  useRoomState,
  useScreenShareParticipant,
  useSelectedParticipant,
  useTrack,
  useVideo,
} from 'hooks';
import { getPreferredFullName, parseParticipantIdentity } from 'utils';
import { MicOffIcon, NQIcon, PinIcon, StarIcon, UserIcon, PipIcon, VideoCameraOffIcon } from 'assets';
import { ParticipantDotMenu, ReconnectingAnimation } from 'components';
import { IMutedParticipantsContext } from 'context';
import { ApiUser, ApiWaitingRoomLinkProfile } from 'types';
import { useAppSelector } from 'store';
import { selectWaitingRoomProfile } from 'store/waitingRoom';
import { selectUser } from 'store/user';
import { useTranslation } from 'react-i18next';

import styles from './MainParticipantInfo.module.scss';

interface MainParticipantInfoProps {
  participant: Participant;
  children: React.ReactNode;
  className?: string;
  showZoomRail?: boolean;
  enableZoomPan?: boolean;
  zoomLevel?: number;
  zoomScale?: number;
  panOffset?: {
    x: number;
    y: number;
  };
  onZoomLevelChange?: (value: number) => void;
  onPanOffsetChange?: (value: { x: number; y: number }) => void;
}

export function MainParticipantInfo({
  participant,
  children,
  className = '',
  showZoomRail = false,
  enableZoomPan = false,
  zoomLevel = 1,
  zoomScale = 1,
  panOffset = { x: 0, y: 0 },
  onZoomLevelChange,
  onPanOffsetChange,
}: MainParticipantInfoProps) {
  const { t } = useTranslation('providerRoom');
  const isGuestCall = useIsGuestCall();
  const forceLightThemeForGuest = isGuestCall && localStorage.getItem('app-theme') === null;
  const { room, isSharingScreen } = useVideo();
  const { isHost } = useRoomHost();
  const participantDisplayingSharedScreen = useScreenShareParticipant();
  const localParticipant = room!.localParticipant;
  const isLocal = localParticipant === participant;
  const [id, identity] = useMemo(() => {
    return parseParticipantIdentity(participant);
  }, [participant]);
  const [identityDisplayingSharedScreenId, identityDisplayingSharedScreen] = useMemo(() => {
    return participantDisplayingSharedScreen ? parseParticipantIdentity(participantDisplayingSharedScreen) : [];
  }, [participantDisplayingSharedScreen]);
  const { mutedParticipantsId }: IMutedParticipantsContext = useMutedParticipants();

  const publications = usePublications(participant);
  const [selectedParticipant] = useSelectedParticipant();
  const videoPublication = publications.find((p) => !p.trackName.includes('screen') && p.kind === 'video');
  const screenSharePublication = publications.find((p) => p.trackName.includes('screen'));

  const audioPublication = publications.find((p) => p.kind === 'audio');
  const audioTrack = useTrack(audioPublication) as RemoteAudioTrack | undefined;
  const isAudioEnabled = useIsTrackEnabled(audioTrack as RemoteAudioTrack);

  const cameraTrack = useTrack(videoPublication || undefined) as LocalVideoTrack | RemoteVideoTrack | undefined;
  const isCameraTrackEnabled = useIsTrackEnabled(cameraTrack as LocalVideoTrack | RemoteVideoTrack);
  const isCameraSwitchedOff = useIsTrackSwitchedOff(cameraTrack as LocalVideoTrack | RemoteVideoTrack);
  const isCameraOff = !videoPublication || !isCameraTrackEnabled || isCameraSwitchedOff;

  const videoTrack = useTrack(screenSharePublication || videoPublication);
  const isVideoEnabled = Boolean(videoTrack);
  const isVideoTrackEnabled = useIsTrackEnabled(videoTrack as LocalVideoTrack | RemoteVideoTrack);
  const isVideoSwitchedOff = useIsTrackSwitchedOff(videoTrack as LocalVideoTrack | RemoteVideoTrack);
  const isVideoOff = !isVideoEnabled || !isVideoTrackEnabled || isVideoSwitchedOff;
  const isParticipantReconnecting = useParticipantIsReconnecting(participant);
  const nq: number = useParticipantNQ(participant);

  const roomState = useRoomState();
  const isReconnecting = roomState === 'reconnecting';

  // const isRecording = useIsRecording();

  const user: ApiUser | null = useAppSelector(selectUser);
  const waitingRoomProfile: ApiWaitingRoomLinkProfile | null = useAppSelector(selectWaitingRoomProfile);

  const { clinicUser } = user || {};
  const { preferedDisplayName: clinicUserPreferredDisplayName } = clinicUser || {};
  const { preferedDisplayName: waitingRoomProfilePreferredDisplayName, providerId } = waitingRoomProfile || {};

  const participantMuted: boolean = useMemo(() => {
    const isMuted: boolean = mutedParticipantsId.some((mutedParticipantId: string) => mutedParticipantId === id);
    return isMuted;
  }, [id, mutedParticipantsId]);

  const showStar: boolean = useMemo(() => {
    return (isHost && id === user?.clinicUser.id) || (!isHost && id === waitingRoomProfile?.providerId);
  }, [isHost, id, waitingRoomProfile, user]);

  const preferredFullName = useMemo(() => {
    const clinicUserFullName = getPreferredFullName(clinicUserPreferredDisplayName, null);
    const waitingRoomFullName = getPreferredFullName(waitingRoomProfilePreferredDisplayName, identity);

    return showStar ? clinicUserFullName ?? waitingRoomFullName : identity;
  }, [isHost, id, waitingRoomProfilePreferredDisplayName, clinicUserPreferredDisplayName]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const panDragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startPanX: number;
    startPanY: number;
    dragging: boolean;
  } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const lastTapRef = useRef<number | null>(null);
  const isPanEnabled = enableZoomPan && zoomScale > 1;

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) {
      return;
    }

    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch {
        // no-op
      }
      return;
    }

    try {
      await containerRef.current.requestFullscreen();
    } catch {
      // Safari may block silently
    }
  }, []);

  const isMobile = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }, []);

  const handleMobileTap = () => {
    const now = Date.now();

    if (lastTapRef.current && now - lastTapRef.current < 300) {
      toggleFullscreen();
      lastTapRef.current = null;
    } else {
      lastTapRef.current = now;
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const [isPiP, setIsPiP] = useState(false);

  useEffect(() => {
    const handlePiPChange = () => {
      const video = containerRef.current?.querySelector('video') as HTMLVideoElement | null;

      setIsPiP(Boolean(video && document.pictureInPictureElement === video));
    };

    document.addEventListener('enterpictureinpicture', handlePiPChange);
    document.addEventListener('leavepictureinpicture', handlePiPChange);

    return () => {
      document.removeEventListener('enterpictureinpicture', handlePiPChange);
      document.removeEventListener('leavepictureinpicture', handlePiPChange);
    };
  }, []);

  const togglePiP = async () => {
    if (!document.pictureInPictureEnabled) {
      return;
    }

    const video = containerRef.current?.querySelector('video') as HTMLVideoElement | null;

    if (!video) {
      return;
    }

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    } catch {
      // Safari / browser restrictions
    }
  };

  const handleZoomChange = useCallback(
    (_event: Event, value: number | number[]) => {
      onPanOffsetChange?.({ x: 0, y: 0 });
      onZoomLevelChange?.(Number(value));
    },
    [onPanOffsetChange, onZoomLevelChange],
  );

  const clamp = useCallback((value: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, value));
  }, []);

  const getPanLimits = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) {
      return { x: 0, y: 0 };
    }

    return {
      x: ((zoomScale - 1) * rect.width) / 2,
      y: ((zoomScale - 1) * rect.height) / 2,
    };
  }, [zoomScale]);

  const handlePanPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isPanEnabled || !onPanOffsetChange) {
        return;
      }

      if (
        !(event.target instanceof HTMLElement) ||
        event.target.closest(`.${styles.zoomRail}`) ||
        event.target.closest(`.${styles.addInfo}`)
      ) {
        return;
      }

      panDragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startPanX: panOffset.x,
        startPanY: panOffset.y,
        dragging: true,
      };

      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    [isPanEnabled, onPanOffsetChange, panOffset.x, panOffset.y],
  );

  const handlePanPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const dragState = panDragStateRef.current;
      if (!dragState?.dragging || dragState.pointerId !== event.pointerId || !onPanOffsetChange) {
        return;
      }

      const limits = getPanLimits();
      const nextPanX = clamp(dragState.startPanX + (event.clientX - dragState.startX), -limits.x, limits.x);
      const nextPanY = clamp(dragState.startPanY + (event.clientY - dragState.startY), -limits.y, limits.y);

      onPanOffsetChange({ x: nextPanX, y: nextPanY });
    },
    [clamp, getPanLimits, onPanOffsetChange],
  );

  const handlePanPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const dragState = panDragStateRef.current;
    if (dragState?.pointerId === event.pointerId) {
      panDragStateRef.current = null;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // no-op
      }
    }
  }, []);

  useEffect(() => {
    if (!isPanEnabled) {
      panDragStateRef.current = null;
    }
  }, [isPanEnabled]);

  return (
    <Box
      ref={containerRef}
      onDoubleClick={!isMobile ? toggleFullscreen : undefined}
      onClick={isMobile ? handleMobileTap : undefined}
      onPointerDown={handlePanPointerDown}
      onPointerMove={handlePanPointerMove}
      onPointerUp={handlePanPointerUp}
      onPointerCancel={handlePanPointerUp}
      className={classnames(styles.container, className, {
        [styles.guestLight]: forceLightThemeForGuest,
        [styles.zoomPanActive]: isPanEnabled,
      })}
      flex={1}
      sx={{
        border: '1px solid var(--app-video-pane-outline)',
        boxShadow: 'var(--app-video-pane-shadow)',
        backgroundColor: isVideoOff
          ? forceLightThemeForGuest
            ? '#F3F3F3'
            : 'var(--app-video-muted)'
          : 'var(--app-page-bg)',
      }}
    >
      {identityDisplayingSharedScreenId && (
        <Box className={classnames(styles.screenSharingInfoContainer)}>
          <Box component='span' className={classnames(styles.screenSharingInfo)}>
            {isSharingScreen
              ? t('youArePresentingToEveryone')
              : t('identityIsPresenting', { identity: identityDisplayingSharedScreen })}
          </Box>
        </Box>
      )}

      {showZoomRail && (
        <Box className={styles.zoomRail} aria-label='EMDR zoom control'>
          <Typography variant='body5' component='span' className={styles.zoomRailLabel}>
            Zoom
          </Typography>
          <Slider
            orientation='vertical'
            className={styles.zoomSlider}
            min={1}
            max={5}
            step={1}
            value={zoomLevel}
            onChange={handleZoomChange}
            marks
            valueLabelDisplay='off'
            aria-label='Zoom'
          />
        </Box>
      )}

      <Box className={styles.infoContainer}>
        <Box className={styles.infoRowBottom}>
          {!screenSharePublication && !isLocal && (
            <Box component='span' className={styles.identity}>
              {selectedParticipant === participant && <PinIcon className={styles.star} />}
              {(!isAudioEnabled || participantMuted) && <MicOffIcon className={styles.micOff} />}
              {isCameraOff && <VideoCameraOffIcon className={styles.cameraOff} />}
              {showStar && <StarIcon className={styles.star} />}
              <Typography variant='body1' component='span' sx={{ color: '#FFF' }}>
                {preferredFullName}
              </Typography>
            </Box>
          )}
          <Box component='span' className={styles.addInfo}>
            <div className={styles.nq}>
              <NQIcon nq={nq} />
            </div>

            <button
              type='button'
              onClick={togglePiP}
              className={classnames(styles.pipButton, {
                [styles.pipButtonActive]: isPiP,
              })}
              aria-label={isPiP ? t('exitPictureInPicture') : t('pictureInPicture')}
            >
              <PipIcon
                sx={{
                  color: '#FFF',
                  opacity: isPiP ? 1 : 0.8,
                }}
              />
            </button>

            {!screenSharePublication && isHost && (
              <ParticipantDotMenu
                withMute
                withRemove
                participant={participant as RemoteParticipant}
                toggleClassName={styles.dotMenuToggle}
                iconClassName={styles.dotMenuIcon}
                iconColor={forceLightThemeForGuest ? '#393534' : '#FFF'}
                menuBackgroundColor={forceLightThemeForGuest ? '#FFFFFF' : 'rgba(0, 0, 0, 0.5)'}
                listItemTextColor={forceLightThemeForGuest ? '#393534' : '#FFF'}
                tooltipPlacement='right-end'
              />
            )}
          </Box>
        </Box>
        {/* {isRecording && (
          <Tooltip
            title="All participants' audio and video is currently being recorded. Visit the app settings to stop recording."
            placement='top'
          >
            <Box className={styles.recordingIndicator} sx={{ backgroundColor: 'rgb(0 0 0 / 50%)' }}>
              <Box className={styles.circle}></Box>
              <Typography variant='body1' sx={{ color: '#FFF' }} data-cy-recording-indicator>
                Recording
              </Typography>
            </Box>
          </Tooltip>
        )} */}
      </Box>
      <Box className={styles.innerContainer}>
        {isVideoOff && <UserIcon sx={{ color: 'var(--app-text-muted)', fontSize: '8.8rem' }} />}
        {(isParticipantReconnecting || isReconnecting) && <ReconnectingAnimation />}
        {children}
      </Box>
    </Box>
  );
}
